import React, { useState } from 'react';
import { Check, Sun, Bell, Target } from 'lucide-react';

const TODAY = new Date().toISOString().slice(0, 10);

const STEP_COUNT = 5;

function Pip({ active, done }) {
  return (
    <div style={{
      width: active ? 20 : 6, height: 6, borderRadius: 3,
      background: done || active ? 'var(--accent)' : 'var(--s3)',
      transition: 'all .25s ease',
    }} />
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: 'var(--s1)', borderRadius: 20,
      padding: '28px 24px', width: '100%', boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, color: 'var(--t3)',
      letterSpacing: '0.5px', textTransform: 'lowercase', marginBottom: 6,
    }}>{children}</div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', height: 44, border: '0.5px solid var(--s3)', borderRadius: 10,
  padding: '0 14px', fontSize: 15, color: 'var(--ink)', background: 'var(--bg)',
  fontFamily: 'var(--sans)', outline: 'none', boxSizing: 'border-box',
};

function Btn({ children, onClick, disabled, secondary }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: '100%', padding: '14px 0', borderRadius: 14, border: 'none',
      background: secondary ? 'transparent' : disabled ? 'var(--s3)' : 'var(--accent)',
      color: secondary ? 'var(--t3)' : disabled ? 'var(--t4)' : 'var(--on-accent)',
      fontFamily: 'var(--sans)', fontSize: 15, fontWeight: secondary ? 400 : 500,
      cursor: disabled ? 'default' : 'pointer',
      transition: 'opacity .1s', WebkitTapHighlightColor: 'transparent',
    }}>{children}</button>
  );
}

// Step 0 — Welcome
function StepWelcome({ onNext }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 52, marginBottom: 12 }}>◉</div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 28, fontWeight: 300, color: 'var(--ink)', marginBottom: 8 }}>
        meet mylo
      </div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--t3)', lineHeight: 1.6, marginBottom: 32 }}>
        your personal chief of staff.<br/>
        two minutes to set up, useful all day.
      </div>
      <Btn onClick={onNext}>get started →</Btn>
    </div>
  );
}

// Step 1 — Profile
function StepProfile({ data, onChange, onNext, onBack }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 20, fontWeight: 400, color: 'var(--ink)', marginBottom: 4 }}>about you</div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--t3)', marginBottom: 24 }}>so mylo can greet you properly</div>
      <Card>
        <Field label="your name">
          <input style={inputStyle} placeholder="e.g. Kyle" value={data.name}
            onChange={e => onChange('name', e.target.value)} autoFocus />
        </Field>
        <Field label="timezone">
          <select style={{ ...inputStyle, background: 'var(--bg)' }}
            value={data.timezone} onChange={e => onChange('timezone', e.target.value)}>
            {['America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
              'America/Phoenix','Europe/London','Europe/Paris','Asia/Tokyo','Australia/Sydney'].map(tz => (
              <option key={tz} value={tz}>{tz.replace('_',' ')}</option>
            ))}
          </select>
        </Field>
      </Card>
      <div style={{ marginTop: 16 }}>
        <Btn onClick={onNext} disabled={!data.name.trim()}>continue →</Btn>
      </div>
      <div style={{ marginTop: 8 }}>
        <Btn secondary onClick={onBack}>← back</Btn>
      </div>
    </div>
  );
}

// Step 2 — Race / training goal
function StepRace({ data, onChange, onNext, onBack }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 20, fontWeight: 400, color: 'var(--ink)', marginBottom: 4 }}>next race</div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--t3)', marginBottom: 24 }}>mylo will track your countdown and training</div>
      <Card>
        <Field label="race name">
          <input style={inputStyle} placeholder="e.g. Brooklyn Marathon"
            value={data.raceName} onChange={e => onChange('raceName', e.target.value)} />
        </Field>
        <Field label="race date">
          <input type="date" style={inputStyle}
            value={data.raceDate} onChange={e => onChange('raceDate', e.target.value)} />
        </Field>
        <Field label="weekly mileage goal">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="number" min="0" max="200" style={{ ...inputStyle, width: 100 }}
              placeholder="20" value={data.weeklyMileageGoal}
              onChange={e => onChange('weeklyMileageGoal', e.target.value)} />
            <span style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--t3)' }}>miles / week</span>
          </div>
        </Field>
      </Card>
      <div style={{ marginTop: 16 }}>
        <Btn onClick={onNext}>continue →</Btn>
      </div>
      <div style={{ marginTop: 8 }}>
        <Btn secondary onClick={onBack}>← back</Btn>
      </div>
      <div style={{ textAlign: 'center', marginTop: 6 }}>
        <button onClick={onNext} style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--t4)', cursor: 'pointer' }}>
          skip for now
        </button>
      </div>
    </div>
  );
}

// Step 3 — Finance
function StepFinance({ data, onChange, onNext, onBack }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 20, fontWeight: 400, color: 'var(--ink)', marginBottom: 4 }}>finances</div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--t3)', marginBottom: 24 }}>a rough picture — mylo won't judge</div>
      <Card>
        <Field label="monthly take-home pay">
          <div style={{ display: 'flex', alignItems: 'center', border: '0.5px solid var(--s3)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg)', height: 44 }}>
            <span style={{ padding: '0 14px', fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--t3)' }}>$</span>
            <input type="number" style={{ ...inputStyle, border: 'none', borderRadius: 0, paddingLeft: 0 }}
              placeholder="0" value={data.monthlyIncome}
              onChange={e => onChange('monthlyIncome', e.target.value)} />
          </div>
        </Field>
        <Field label="biggest recurring expense">
          <input style={inputStyle} placeholder="e.g. rent, mortgage"
            value={data.topExpenseName} onChange={e => onChange('topExpenseName', e.target.value)} />
        </Field>
        <Field label="amount">
          <div style={{ display: 'flex', alignItems: 'center', border: '0.5px solid var(--s3)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg)', height: 44 }}>
            <span style={{ padding: '0 14px', fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--t3)' }}>$</span>
            <input type="number" style={{ ...inputStyle, border: 'none', borderRadius: 0, paddingLeft: 0 }}
              placeholder="0" value={data.topExpenseAmount}
              onChange={e => onChange('topExpenseAmount', e.target.value)} />
          </div>
        </Field>
      </Card>
      <div style={{ marginTop: 16 }}>
        <Btn onClick={onNext}>continue →</Btn>
      </div>
      <div style={{ marginTop: 8 }}>
        <Btn secondary onClick={onBack}>← back</Btn>
      </div>
      <div style={{ textAlign: 'center', marginTop: 6 }}>
        <button onClick={onNext} style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--t4)', cursor: 'pointer' }}>
          skip for now
        </button>
      </div>
    </div>
  );
}

// Step 4 — Notifications
function StepNotifications({ onNext, onBack, onRequestPush }) {
  const [status, setStatus] = useState('idle'); // idle | requesting | granted | denied
  const handleEnable = async () => {
    setStatus('requesting');
    const ok = await onRequestPush();
    setStatus(ok ? 'granted' : 'denied');
  };
  return (
    <div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 20, fontWeight: 400, color: 'var(--ink)', marginBottom: 4 }}>stay in the loop</div>
      <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--t3)', marginBottom: 24 }}>mylo sends a morning brief and smart nudges</div>
      <Card>
        {status === 'granted' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: 8, display:'flex', justifyContent:'center' }}>
              <div style={{ width:44, height:44, borderRadius:'50%', background:'color-mix(in srgb, var(--accent) 12%, transparent)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Check size={22} strokeWidth={1.5} color="var(--accent)" />
              </div>
            </div>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--accent)' }}>notifications enabled</div>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>you'll get a morning brief at 7am</div>
          </div>
        ) : status === 'denied' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--t3)', lineHeight: 1.6 }}>
              notifications blocked. enable them in your browser settings to get briefings.
            </div>
          </div>
        ) : (
          <div>
            {[
              { Icon: Sun,    text: 'morning brief at 7am — tasks, schedule, and insights' },
              { Icon: Target, text: 'afternoon nudge when something needs attention' },
              { Icon: Bell,   text: "evening check-in with tomorrow's preview" },
            ].map(({ Icon, text }, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '10px 0', borderBottom: i < 2 ? '0.5px solid var(--s2)' : 'none' }}>
                <div style={{ flexShrink: 0, marginTop: 2, color: 'var(--accent)', opacity: 0.7 }}>
                  <Icon size={15} strokeWidth={1.5} />
                </div>
                <span style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--t2)', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
            <div style={{ marginTop: 20 }}>
              <Btn onClick={handleEnable} disabled={status === 'requesting'}>
                {status === 'requesting' ? 'requesting…' : 'enable notifications'}
              </Btn>
            </div>
          </div>
        )}
      </Card>
      <div style={{ marginTop: 16 }}>
        <Btn onClick={onNext}>{status === 'granted' ? 'finish setup →' : 'continue →'}</Btn>
      </div>
      {status !== 'granted' && (
        <div style={{ marginTop: 8 }}>
          <Btn secondary onClick={onBack}>← back</Btn>
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: 6 }}>
        <button onClick={onNext} style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--t4)', cursor: 'pointer' }}>
          skip for now
        </button>
      </div>
    </div>
  );
}

export function OnboardingFlow({ onComplete, onRequestPush }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({
    name: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
  });
  const [race, setRace] = useState({ raceName: '', raceDate: '', weeklyMileageGoal: '' });
  const [finance, setFinance] = useState({ monthlyIncome: '', topExpenseName: '', topExpenseAmount: '' });

  const updateProfile  = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const updateRace     = (k, v) => setRace(p => ({ ...p, [k]: v }));
  const updateFinance  = (k, v) => setFinance(p => ({ ...p, [k]: v }));

  const finish = () => {
    const now = Date.now();
    const financeItems = [];
    if (finance.topExpenseName && finance.topExpenseAmount) {
      financeItems.push({
        id: `f${now}`, name: finance.topExpenseName,
        amount: parseFloat(finance.topExpenseAmount) || 0,
        category: 'housing', date: TODAY, type: 'expense',
      });
    }
    const incomeItems = [];
    if (finance.monthlyIncome) {
      incomeItems.push({
        id: `i${now}`, name: 'Primary income',
        amount: parseFloat(finance.monthlyIncome) || 0,
        freq: 'monthly',
        nextDate: new Date(now + 14 * 86400000).toISOString().slice(0, 10),
      });
    }
    const goals = [];
    if (race.raceName && race.raceDate) {
      goals.push({
        id: `g${now}`, type: 'race', title: race.raceName,
        targetDate: race.raceDate, progress: 0, icon: 'Flag',
      });
    }
    const tasks = [{
      id: `t${now}`, text: 'explore mylo →', bucket: 'today',
      dueDate: TODAY, done: false, created: TODAY, priority: 'medium',
      listId: 'l1', recurring: 'none',
    }];
    onComplete({
      profile: {
        ...profile,
        name: profile.name.trim(),
        onboarded: true,
        weeklyMileageGoal: parseFloat(race.weeklyMileageGoal) || 0,
      },
      raceName: race.raceName.trim(),
      raceDate: race.raceDate,
      finance: financeItems,
      income: incomeItems,
      goals,
      tasks,
      memories: [],
    });
  };;

  const steps = [
    <StepWelcome onNext={() => setStep(1)} />,
    <StepProfile data={profile} onChange={updateProfile} onNext={() => setStep(2)} onBack={() => setStep(0)} />,
    <StepRace data={race} onChange={updateRace} onNext={() => setStep(3)} onBack={() => setStep(1)} />,
    <StepFinance data={finance} onChange={updateFinance} onNext={() => setStep(4)} onBack={() => setStep(2)} />,
    <StepNotifications onNext={finish} onBack={() => setStep(3)} onRequestPush={onRequestPush || (() => Promise.resolve(false))} />,
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'flex-start', zIndex: 9999, overflowY: 'auto',
      padding: '48px 20px 40px', boxSizing: 'border-box',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Progress pips */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 40 }}>
          {Array.from({ length: STEP_COUNT }, (_, i) => (
            <Pip key={i} active={i === step} done={i < step} />
          ))}
        </div>
        {steps[step]}
      </div>
    </div>
  );
}
